import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, query, where, orderBy, getDocs, getDoc,
  addDoc, setDoc, deleteDoc, onSnapshot, serverTimestamp, doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from "sonner";

export function useChatSystem(userId) {
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [chatError, setChatError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const chatBoxRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (chatBoxRef.current) chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }, 100);
  }, []);

  // Fetch chats
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const q = query(collection(db, 'chats'), where('users', 'array-contains', userId), orderBy('timestamp', 'desc'));
        const snap = await getDocs(q);
        const list = await Promise.all(snap.docs.map(async (docSnap) => {
          const d = docSnap.data();
          const otherId = d.users.find((uid) => uid !== userId);
          const otherSnap = await getDoc(doc(db, 'users', otherId));
          const other = otherSnap.exists() ? otherSnap.data() : null;
          return {
            id: docSnap.id, otherUserId: otherId,
            otherUsername: other?.username || 'Bilinmiyor',
            otherPhoto: other?.profilePhoto || null,
            lastMessage: d.lastMessage,
            timestamp: d.timestamp?.toDate().toLocaleString() || '',
          };
        }));
        setChats(list);
      } catch (err) { setChatError('Sohbetler yüklenemedi.'); }
    })();
  }, [userId]);

  // Listen to messages
  useEffect(() => {
    if (!selectedChat) { setMessages([]); return; }
    const chatId = [userId, selectedChat.otherUserId].sort().join('_');
    const q = query(collection(db, 'chats', chatId, 'messages'), orderBy('timestamp'));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      scrollToBottom();
    }, (err) => setChatError(`Mesajlar yüklenemedi: ${err.message}`));
    return () => unsub();
  }, [selectedChat, userId, scrollToBottom]);

  const sendMessage = async () => {
    if (!text.trim() || !selectedChat) return;
    const chatId = [userId, selectedChat.otherUserId].sort().join('_');
    try {
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        text, from: userId, to: selectedChat.otherUserId, timestamp: serverTimestamp(),
      });
      await import('firebase/firestore').then(() =>
        setDoc(doc(db, 'chats', chatId), { lastMessage: text, timestamp: serverTimestamp() }, { merge: true })
      );
      setText('');
      scrollToBottom();
    } catch (err) { setChatError(`Mesaj gönderilemedi: ${err.message}`); }
  };

  const searchUsers = async (queryText) => {
    
    setSearchQuery(queryText);
    if (!queryText.trim()) { setSearchResults([]); return; }
    try {
      const snap = await getDocs(collection(db, 'users'));
      setSearchResults(snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => u.id !== userId && u.username?.toLowerCase().includes(queryText.toLowerCase()))
      );
    } catch { setChatError('Kullanıcı aranırken hata oluştu.'); }
  };

  const startChat = async (user) => {
    
    const chatId = [userId, user.id].sort().join('_');
    try {
      const chatSnap = await getDoc(doc(db, 'chats', chatId));
      if (!chatSnap.exists()) {
        await setDoc(doc(db, 'chats', chatId), { users: [userId, user.id], lastMessage: '', timestamp: serverTimestamp() });
      }
      const chatObj = { id: chatId, otherUserId: user.id, otherUsername: user.username, otherPhoto: user.profilePhoto || null, lastMessage: '', timestamp: new Date().toLocaleString() };
      setSelectedChat(chatObj);
      setChats((prev) => prev.find((c) => c.otherUserId === user.id) ? prev : [chatObj, ...prev]);
      setSearchQuery(''); setSearchResults([]);
    } catch { setChatError('Sohbet başlatılamadı.'); }
  };

  const deleteChat = async (chatId) => {
    try {
      await deleteDoc(doc(db, 'chats', chatId));
      setChats((prev) => prev.filter((c) => c.id !== chatId));
      if (selectedChat?.id === chatId) setSelectedChat(null);
      toast.success('Sohbet silindi.');
    } catch (err) { setChatError(`Sohbet silinemedi: ${err.message}`); }
  };

  const closeChat = () => { setSelectedChat(null); setSearchQuery(''); setSearchResults([]); };

  return {
    chats, selectedChat, setSelectedChat, messages, text, setText,
    chatError, searchQuery, searchResults, chatBoxRef,
    sendMessage, searchUsers, startChat, deleteChat, closeChat,
  };
}
